import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import { Select } from 'antd';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CloseIcon } from 'components/icons';
import { useUpdateSimTags } from 'lib/hooks/sim/sim.mutations';
import { useAllSimTags } from 'lib/hooks/sim/sim.queries';
import { SimType } from 'types/shared';
import { useModalsContext } from 'views/YourSiMs/hooks';

const addTagsFormSchema = z.object({
  tags: z.array(z.string().min(3, 'Tags should be at least 3 characters long')),
});

type AddTagsFormSchema = z.infer<typeof addTagsFormSchema>;

type AddTagsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function AddTagsModal({ isOpen, onClose }: AddTagsModalProps) {
  const { data: allSimTags } = useAllSimTags({ simType: SimType.SPR });
  const updateSimTagsMutation = useUpdateSimTags();
  const { targetSim } = useModalsContext();

  const { setValue, formState, ...form } = useForm<AddTagsFormSchema>({
    defaultValues: { tags: [] },
    resolver: zodResolver(addTagsFormSchema),
    reValidateMode: 'onChange',
  });

  // Update the form field with existing tags if the target sim already has tags
  useEffect(() => {
    if (!targetSim) return;

    const existingTags =
      targetSim?.tags?.length > 0 ? targetSim.tags.split(',') : [];

    setValue('tags', existingTags, { shouldValidate: true });
  }, [setValue, targetSim]);

  const closeModalHandler = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (values: AddTagsFormSchema) => {
    if (!targetSim) return;

    const { tags } = values;

    try {
      await updateSimTagsMutation.mutateAsync({
        simIdsWithTags: [{ id: targetSim.id, tags: tags.join(',') }],
      });

      closeModalHandler();
    } catch (error) {
      form.setError('tags', { message: 'Error while saving sim tags' });
    }
  };

  const getErrorMessage = () =>
    formState.errors.tags?.find?.((error) => error?.message)?.message ||
    formState.errors.tags?.message;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModalHandler}
      ariaHideApp={false}
      contentLabel="Add Tags"
      portalClassName="react-modal"
      overlayClassName="modal"
      className="modal-dialog react-modal-dialog-sm modal-dialog-centered modal-create-tag"
    >
      <div className="modal-content">
        <div className="react-modal-header">
          <h5 className="react-modal-title">Add Tags</h5>

          <button
            type="button"
            className="btn react-modal-close"
            onClick={closeModalHandler}
          >
            <CloseIcon className={''} />
          </button>
        </div>

        <div className="react-modal-body">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="form-group material-textfield">
              <Controller
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <Select
                    autoFocus
                    mode="tags"
                    size="large"
                    bordered={false}
                    className="multiple__tags__textfield"
                    dropdownClassName="multiple__tags__textfield__overlay"
                    style={{ width: '100%' }}
                    placeholder="Tag Name"
                    {...field}
                    onSelect={() => form.trigger('tags')}
                    onDeselect={() => form.trigger('tags')}
                  >
                    {allSimTags?.data.map((tag, i) => (
                      <Select.Option key={tag + i} value={tag}>
                        {tag}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />

              <div className="invalid-feedback">{getErrorMessage()}</div>
            </div>
            <button
              type="submit"
              disabled={formState.isSubmitting || !formState.isValid}
              className="btn btn-dark btn-lg w-100"
            >
              {formState.isSubmitting ? 'Saving Tags...' : 'Save'}
            </button>
          </form>
        </div>
      </div>
    </ReactModal>
  );
}

export default AddTagsModal;
